import { defineStore } from "pinia"
import { computed, ref, type Ref } from "vue"
import { useGlobalStore } from "@/stores/global"
import { useCurrentAttackerStore } from "@/stores/currentAttacker"
import { useCurrentDefenderStore } from "@/stores/currentDefender"
import { useModifiersGlobalStore } from "@/stores/modifiersGlobal"
import { useUserConfigSimsStore } from "@/stores/userConfigSims"
import { useCurrentAttackerAdHocStore } from "@/stores/currentAttackerAdHoc"
import { useCurrentDefenderAdHocStore } from "@/stores/currentDefenderAdHoc"
import { useAttackContextStore } from "@/stores/attackContext"
import { configSims } from "~/data/config"
import type { SimResults } from "~/types"

export const useSimStore = defineStore("sim", () => {
  // State
  const simError: Ref<string | null> = ref(null)
  const simLastManualTriggerTime: Ref<number | null> = ref(null)
  const simResultsKeyCached: Ref<string | null> = ref(null)
  const simResultsReady: Ref<boolean> = ref(false)
  const simResultsReadyTime: Ref<number | null> = ref(null)
  const simTriggerLast: Ref<string | null> = ref(null)
  const simulation: Ref<SimResults> = ref({
    simId: null,
    simResults: null,
  } as unknown as SimResults)
  const simulationRunning: Ref<boolean> = ref(false)

  // Getters
  const showAlertFnpFaq = computed(() => {
    const currentDefenderStore = useCurrentDefenderStore()
    const defenderForSim = currentDefenderStore.getDefenderForSim
    if (
      !defenderForSim ||
      !("computed_ignore" in defenderForSim) ||
      !defenderForSim.computed_ignore
    ) {
      return false
    }
    if (!simulation.value.simResults) {
      return false
    }
    // Check if any models were slain during the simulation
    const modelsSlainResults =
      simulation.value.simResults.count.finalModelsSlain
    if (modelsSlainResults && "_custom" in modelsSlainResults) {
      const customValue = modelsSlainResults._custom
      if (customValue.type === "map" && customValue.value) {
        for (const [modelsSlainStr] of Object.entries(customValue.value)) {
          const modelsSlain = parseInt(modelsSlainStr, 10)
          if (modelsSlain > 0) {
            return true
          }
        }
      }
    }
    return false
  })

  const showSimResults = computed(() => {
    return (
      simResultsReady.value &&
      simulation.value.simResults !== null &&
      simReqsMet.value
    )
  })

  const simReqsMet = computed(() => {
    const globalStore = useGlobalStore()
    const currentAttackerStore = useCurrentAttackerStore()
    const currentDefenderStore = useCurrentDefenderStore()
    const currentAttackerAdHocStore = useCurrentAttackerAdHocStore()
    const currentDefenderAdHocStore = useCurrentDefenderAdHocStore()

    // Attacker requirements
    if (globalStore.profileUiAttacker === "adhoc") {
      if (!currentAttackerAdHocStore.confirmed) {
        return false
      }
    } else {
      if (
        !currentAttackerStore.currentAttacker?.id ||
        !currentAttackerStore.currentAttackerCompatibleWithAttackType
      ) {
        return false
      }
      if (
        currentAttackerStore.currentAttackerCompatibleWithAttackType &&
        currentAttackerStore.currentAttackerWeaponsSelectedFiltered.length === 0
      ) {
        return false
      }
    }

    // Defender requirements
    if (globalStore.profileUiDefender === "adhoc") {
      if (!currentDefenderAdHocStore.confirmed) {
        return false
      }
    } else {
      if (!currentDefenderStore.currentDefender?.id) {
        return false
      }
    }

    return true
  })

  const simResultsKey = computed(() => {
    const globalStore = useGlobalStore()
    const currentAttackerStore = useCurrentAttackerStore()
    const currentDefenderStore = useCurrentDefenderStore()
    const modifiersGlobalStore = useModifiersGlobalStore()
    const currentAttackerAdHocStore = useCurrentAttackerAdHocStore()
    const currentDefenderAdHocStore = useCurrentDefenderAdHocStore()
    const attackContextStore = useAttackContextStore()

    if (!simReqsMet.value) {
      return null
    }

    let generatedKey = ""

    if (globalStore.profileUiAttacker === "adhoc") {
      if (typeof currentAttackerAdHocStore.profile.updated !== "undefined") {
        generatedKey += `${currentAttackerAdHocStore.profile.updated}`
      }
    }

    if (globalStore.profileUiDefender === "adhoc") {
      if (typeof currentDefenderAdHocStore.profile.updated !== "undefined") {
        generatedKey += `:${currentDefenderAdHocStore.profile.updated}`
      }
    }

    generatedKey += attackContextStore.attackContext.attackType

    // Handle different attack contexts with their specific properties
    if (attackContextStore.attackContext.attackType === "Melee") {
      const meleeContext = attackContextStore.attackContext.melee
      for (const [key, value] of Object.entries(meleeContext)) {
        generatedKey += `:${String(key)}=${String(value)}`
      }
    } else {
      const rangedContext = attackContextStore.attackContext.ranged
      for (const [key, value] of Object.entries(rangedContext)) {
        generatedKey += `:${String(key)}=${String(value)}`
      }
    }

    if (globalStore.profileUiAttacker === "profile") {
      if (
        typeof currentAttackerStore.currentAttacker !== "undefined" &&
        typeof currentAttackerStore.currentAttacker.id !== "undefined"
      ) {
        generatedKey += `:${currentAttackerStore.currentAttacker.id}`
        generatedKey += `${currentAttackerStore.currentAttacker.updated}`
        if (
          typeof currentAttackerStore.currentAttackerWeaponsSelected !==
          "undefined"
        ) {
          currentAttackerStore.currentAttackerWeaponsSelected.forEach(
            (weapon) => {
              generatedKey += `:${weapon.id}`
            }
          )
        }
        if (
          attackContextStore.attackContext.attackType === "Melee" &&
          Array.isArray(
            currentAttackerStore.currentAttacker.sim
              ?.abilities_selected_attacker_melee
          )
        ) {
          currentAttackerStore.currentAttacker.sim.abilities_selected_attacker_melee.forEach(
            (abilityId) => {
              generatedKey += `:${abilityId}`
            }
          )
        }
        if (
          attackContextStore.attackContext.attackType === "Ranged" &&
          Array.isArray(
            currentAttackerStore.currentAttacker.sim
              ?.abilities_selected_attacker_ranged
          )
        ) {
          currentAttackerStore.currentAttacker.sim.abilities_selected_attacker_ranged.forEach(
            (abilityId) => {
              generatedKey += `:${abilityId}`
            }
          )
        }
      }
    }

    if (globalStore.profileUiDefender === "profile") {
      if (
        typeof currentDefenderStore.currentDefender !== "undefined" &&
        typeof currentDefenderStore.currentDefender.id !== "undefined"
      ) {
        generatedKey += `:${currentDefenderStore.currentDefender.id}`
        generatedKey += `:${currentDefenderStore.currentDefender.updated}`
        if (
          Array.isArray(
            currentDefenderStore.currentDefender.sim
              ?.abilities_selected_defender
          )
        ) {
          currentDefenderStore.currentDefender.sim.abilities_selected_defender.forEach(
            (abilityId) => {
              generatedKey += `:${abilityId}`
            }
          )
        }
      }
    }

    if (typeof modifiersGlobalStore.modifiersGlobalSelected !== "undefined") {
      modifiersGlobalStore.modifiersGlobalSelected.forEach((modifier) => {
        generatedKey += `:${modifier.id}:${modifier.updated}`
      })
    }

    generatedKey += `:${simLastManualTriggerTime.value}`

    return generatedKey
  })

  const showDamageNotIgnored = computed(() => {
    const currentDefenderStore = useCurrentDefenderStore()
    const modifiersGlobalStore = useModifiersGlobalStore()

    const defenderForSim = currentDefenderStore.getDefenderForSim
    if (defenderForSim?.units) {
      for (const unit of defenderForSim.units) {
        if (!unit.model_types) {
          // Skip this unit if model_types is undefined
          continue
        }
        for (const modelType of unit.model_types) {
          if (modelType.ignore !== undefined && modelType.ignore !== null) {
            return true
          }
        }
      }
    }

    for (const defenderAbility of currentDefenderStore.getDefenderAbilitiesForSim) {
      if (defenderAbility.effect.type === "feelNoPain") return true
    }

    for (const globalModifier of modifiersGlobalStore.globalModifiersFilteredBySelected) {
      if (globalModifier.effect.type === "feelNoPain") return true
    }

    return false
  })

  // Actions
  function endSim() {
    simulationRunning.value = false
    simResultsReady.value = true
    simResultsReadyTime.value = Date.now()
    simError.value = null
  }

  function tryRunSim(trigger: "auto" | "manual") {
    if (!simReqsMet.value) {
      return
    }

    const userConfigSimsStore = useUserConfigSimsStore()

    if (trigger === "auto") {
      if (!userConfigSimsStore.autoRunSims) {
        return
      }
      if (userConfigSimsStore.totalSims > configSims.maxAutoRunSims) {
        return
      }
    }

    if (trigger === "manual") {
      simLastManualTriggerTime.value = Date.now()
    }

    simTriggerLast.value = trigger
    simResultsKeyCached.value = simResultsKey.value
  }

  return {
    // State
    simError,
    simLastManualTriggerTime,
    simResultsKeyCached,
    simResultsReady,
    simResultsReadyTime,
    simTriggerLast,
    simulation,
    simulationRunning,
    // Getters
    showAlertFnpFaq,
    showSimResults,
    simReqsMet,
    simResultsKey,
    showDamageNotIgnored,
    // Actions
    endSim,
    tryRunSim,
  }
})
